import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  Row,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  SxProps,
  Theme,
  Box
} from '@mui/material';
import TableStatusView from './TableStatusView';
import { Fragment, ReactNode } from 'react';

interface DataTableProps<Data> {
  data?: Data[];
  /** Column definitions. Use size, minSize, maxSize on each column to set width (e.g. size: 200). Columns are resizable by default. */
  columns: ColumnDef<Data>[];
  isHeaderSticky?: boolean;
  isHideHeader?: boolean;
  /** When false, columns fill available width and no horizontal scroll is forced. Default true. */
  enableColumnResizing?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  isLoading?: boolean;
  containerStyles?: SxProps<Theme>;
  renderAfterRow?: (row: Row<Data>, index: number) => ReactNode;
}

export default function DataTable<Data>({
  data = [],
  columns,
  isHeaderSticky = true,
  isHideHeader = false,
  enableColumnResizing = true,
  sorting,
  onSortingChange,
  isLoading,
  containerStyles,
  renderAfterRow
}: DataTableProps<Data>) {
  const theme = useTheme();
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableExpanding: !!renderAfterRow,
    getRowCanExpand: () => !!renderAfterRow,
    state: {
      sorting
    },
    manualPagination: true,
    onSortingChange,
    enableColumnResizing,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 50,
      maxSize: 800,
      size: 150
    }
  });

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: '1px solid #ECECEC',
        borderBottom: 0,
        borderRadius: 2,
        width: '100%',
        maxWidth: '100%',
        overflowX: enableColumnResizing ? 'auto' : 'hidden',
        overflowY: 'visible',
        ...containerStyles
      }}
    >
      <Table
        stickyHeader={isHeaderSticky}
        aria-label='data table'
        sx={{
          tableLayout: 'auto',
          width: '100%',
          ...(enableColumnResizing ? { minWidth: table.getTotalSize() } : {})
        }}
      >
        {!isHideHeader && (
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const size = header.getSize();
                  return (
                    <TableCell
                      key={header.id}
                      align='left'
                      sortDirection={header.column.getIsSorted() ? header.column.getIsSorted() : false}
                      sx={{
                        fontWeight: '600',
                        fontSize: { xs: '12px', lg: '14px' },
                        color: theme.palette.secondary.main,
                        backgroundColor: theme.palette.background.paper,
                        textTransform: 'uppercase',
                        padding: { xs: '12px 8px', lg: '16px' },
                        ...(enableColumnResizing
                          ? { width: size, minWidth: size, boxSizing: 'border-box' }
                          : { whiteSpace: 'nowrap' }),
                        position: 'relative'
                      }}
                    >
                      <div style={{ overflow: 'visible' }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                      {enableColumnResizing && header.column.getCanResize() && (
                        <Box
                          component='span'
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: 8,
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            zIndex: 1,
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              right: 3,
                              top: '10%',
                              height: '80%',
                              width: 2,
                              borderRadius: 1,
                              bgcolor: theme.palette.primary.main,
                              opacity: 0
                            },
                            '&:hover::after, &:active::after': {
                              opacity: 1
                            },
                            '&:active::after': {
                              bgcolor: theme.palette.primary.dark
                            }
                          }}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
        )}
        <TableStatusView
          isLoading={isLoading}
          hasNoRecords={!data.length}
          colCount={table.getHeaderGroups()[0].headers.length}
        />
        <TableBody>
          {table.getRowModel().rows.map((row, index) => (
            <Fragment key={row.id}>
              <TableRow
                sx={{
                  borderBottom: index === table.getRowModel().rows.length - 1 ? 'none' : '1px solid #ECECEC'
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const size = cell.column.getSize();
                  return (
                    <TableCell
                      key={cell.id}
                      sx={{
                        padding: { xs: '12px 8px', lg: '18px' },
                        fontSize: { xs: '14px', lg: '16px' },
                        color: theme.palette.text.primary,
                        ...(enableColumnResizing
                          ? { width: size, minWidth: size, boxSizing: 'border-box' }
                          : { whiteSpace: 'nowrap' })
                      }}
                    >
                      <div style={{ overflow: 'visible' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
              {row.getIsExpanded() && renderAfterRow?.(row, index)}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
